/**
 * PrettyRefWidget — L0 pretty pill for $ref{} references.
 *
 * Design:
 *   Resolved:   r plugin value
 *   Pending:    r plugin id › path
 *   Unresolved: r plugin id › path
 *   Error:      r plugin id
 */

import { WidgetType } from "@codemirror/view";
import type { ExecutionStatus } from "../../../store/plugin-execution-store";
import {
    appendInlineStatus,
    attachSyntaxEditInteractions,
    createHoverDetail,
    createPrettySyntaxShell,
    createSyntaxBadge,
    createTextSpan,
} from "../shared/widgets";
import type { RefStatus } from "../types";
import {
    formatRefDisplayPath,
    type RefAccessor,
    type RefSourceKind,
} from "./resolution";

export class PrettyRefWidget extends WidgetType {
    constructor(
        readonly refContent: string,
        readonly definitionId: string,
        readonly outputPath: string,
        readonly status: RefStatus,
        readonly resolvedValue: unknown,
        readonly accessor: RefAccessor = "output",
        readonly sourceKind: RefSourceKind = "definition",
        readonly pluginName: string | null = null,
        readonly isImported: boolean = false,
        readonly executionStatus: ExecutionStatus = "idle",
        readonly executionDuration: number | null = null,
    ) {
        super();
    }
    eq(other: PrettyRefWidget) {
        return (
            other.refContent === this.refContent &&
            other.definitionId === this.definitionId &&
            other.outputPath === this.outputPath &&
            other.status === this.status &&
            other.resolvedValue === this.resolvedValue &&
            other.accessor === this.accessor &&
            other.sourceKind === this.sourceKind &&
            other.pluginName === this.pluginName &&
            other.isImported === this.isImported &&
            other.executionStatus === this.executionStatus &&
            other.executionDuration === this.executionDuration
        );
    }
    toDOM(): HTMLElement {
        const fullPath = formatRefDisplayPath(this.accessor, this.outputPath);

        const badge = createSyntaxBadge("r", {
            sub: getRefKindBadgeSub(this.status, this.sourceKind),
            sup: this.isImported ? "M" : undefined,
        });
        badge.classList.add("cm-magic-ref-pretty-icon");

        const primary: HTMLElement[] = [];

        if (this.status === "loop-var" || this.status === "scope-var") {
            primary.push(createTextSpan("cm-magic-ref-pretty-id", this.refContent));
        } else if (this.status === "error") {
            primary.push(createTextSpan("cm-magic-ref-pretty-id", this.definitionId));
        } else if (this.status === "resolved" && this.resolvedValue !== undefined) {
            primary.push(
                createTextSpan(
                    "cm-magic-ref-pretty-value",
                    formatPillValue(this.resolvedValue),
                ),
            );
        } else {
            primary.push(createTextSpan("cm-magic-ref-pretty-id", this.definitionId));
        }

        const detail = createHoverDetail("cm-magic-ref-pretty-detail", [
            getRefHoverKind(this.status, this.sourceKind, this.pluginName),
            getRefHoverSummary(
                this.status,
                fullPath,
            ),
        ]);

        const wrapper = createPrettySyntaxShell({
            wrapperClass: `cm-magic-ref-pretty cm-magic-ref-pretty-${this.status} ${getRefSourceClass(this.status, this.sourceKind)}`,
            badge,
            primary,
            detail,
        });
        appendInlineStatus(wrapper, this.executionStatus, this.executionDuration);
        wrapper.title = `${this.definitionId} › ${fullPath}`;
        if (
            this.sourceKind !== "asset" &&
            this.status !== "loop-var" &&
            this.status !== "scope-var"
        ) {
            wrapper.dataset.magicNavTarget = this.definitionId;
            wrapper.dataset.magicNavKind = "ref";
        }
        attachSyntaxEditInteractions(wrapper, { keyboard: false });

        return wrapper;
    }
    ignoreEvent(): boolean {
        return true;
    }
}

function getRefHoverKind(
    status: RefStatus,
    sourceKind: RefSourceKind,
    pluginName: string | null,
): HTMLElement | null {
    if (status === "loop-var") {
        return createTextSpan("cm-magic-hover-kind cm-magic-ref-pretty-kind", "loop");
    }
    if (status === "scope-var") {
        return createTextSpan("cm-magic-hover-kind cm-magic-ref-pretty-kind", "scope");
    }
    if (sourceKind === "definition" && pluginName) {
        return createTextSpan("cm-magic-hover-kind cm-magic-ref-pretty-kind", pluginName);
    }
    if (sourceKind === "asset") {
        return createTextSpan("cm-magic-hover-kind cm-magic-ref-pretty-kind", "asset");
    }
    if (sourceKind === "unit") {
        return createTextSpan("cm-magic-hover-kind cm-magic-ref-pretty-kind", "unit");
    }
    if (sourceKind === "content") {
        return createTextSpan("cm-magic-hover-kind cm-magic-ref-pretty-kind", "content");
    }
    if (sourceKind === "for") {
        return createTextSpan("cm-magic-hover-kind cm-magic-ref-pretty-kind", "for");
    }
    return null;
}

function getRefHoverSummary(
    status: RefStatus,
    fullPath: string,
): HTMLElement | null {
    if (status === "loop-var" || status === "scope-var") {
        return null;
    }

    return createTextSpan(
        "cm-magic-hover-summary cm-magic-ref-pretty-summary",
        fullPath,
    );
}

function getRefSourceClass(
    status: RefStatus,
    sourceKind: RefSourceKind,
): string {
    if (status === "loop-var") return "cm-magic-ref-pretty-source-loop-var";
    if (status === "scope-var") return "cm-magic-ref-pretty-source-scope-var";
    if (sourceKind === "asset") return "cm-magic-ref-pretty-source-asset";
    if (sourceKind === "unit") return "cm-magic-ref-pretty-source-unit";
    if (sourceKind === "content") return "cm-magic-ref-pretty-source-content";
    if (sourceKind === "for") return "cm-magic-ref-pretty-source-for";
    return "cm-magic-ref-pretty-source-definition";
}

function getRefKindBadgeSub(
    status: RefStatus,
    sourceKind: RefSourceKind,
): string {
    if (status === "loop-var") return "F";
    if (status === "scope-var") return "V";
    if (sourceKind === "asset") return "A";
    if (sourceKind === "unit") return "U";
    if (sourceKind === "content") return "C";
    if (sourceKind === "for") return "F";
    return "D";
}

/** Format a value for the compact ref pill (very short) */
export function formatPillValue(value: unknown): string {
    if (value === null) return "null";
    if (value === undefined) return "";
    if (typeof value === "string") {
        return value.length > 40 ? value.slice(0, 37) + "…" : value;
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    if (Array.isArray(value)) {
        return `[${value.length} items]`;
    }
    if (typeof value === "object") {
        const keys = Object.keys(value as Record<string, unknown>);
        return `{${keys.length} keys}`;
    }
    return String(value);
}
